'use client'

import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'

interface SandboxBehaviorCardProps {
    processor: ProcessorDetails
}

export const SandboxBehaviorCard = ({ processor }: SandboxBehaviorCardProps) => {
    const { t } = useTranslation('processors')
    const options = processor.options as Record<string, unknown>
    const forceDecline = options?.forceDecline === true
    const targetApprovalRate = options?.targetApprovalRate as number | undefined

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <span>{t('transactionBehavior')}</span>
                    {forceDecline && <Badge variant="destructive">{t('forceDecline')}</Badge>}
                    {targetApprovalRate !== undefined && !forceDecline && (
                        <Badge variant="secondary">{targetApprovalRate}% {t('approval')}</Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {forceDecline ? (
                        <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                            <h4 className="mb-2 text-sm font-medium text-red-800">
                                🚫 {t('forceDeclineMode')}
                            </h4>
                            <p className="text-sm text-red-700">
                                {t('forceDeclineModeDescription')}
                            </p>
                        </div>
                    ) : targetApprovalRate !== undefined ? (
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <h4 className="mb-2 text-sm font-medium text-blue-800">
                                🎯 {t('targetApprovalRate')}
                            </h4>
                            <p className="text-sm text-blue-700">
                                {t('targetApprovalRateDescription', { rate: targetApprovalRate })}
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-medium text-green-700">{t('successRate')}:</span>
                                    <span className="ml-1 text-green-600">{targetApprovalRate}%</span>
                                </div>
                                <div>
                                    <span className="font-medium text-red-700">{t('declineRate')}:</span>
                                    <span className="ml-1 text-red-600">{100 - targetApprovalRate}%</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-lg bg-green-50 p-4 border border-green-200">
                            <h4 className="mb-2 text-sm font-medium text-green-800">
                                💳 {t('testCardBehavior')}
                            </h4>
                            <p className="text-sm text-green-700">
                                {t('testCardBehaviorDescription')}
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <p className="text-sm text-muted-foreground">{t('forceDecline')}</p>
                            <p className="font-semibold">
                                {forceDecline ? t('enabled') : t('disabled')}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">{t('targetApprovalRate')}</p>
                            <p className="font-semibold">
                                {targetApprovalRate !== undefined
                                    ? `${targetApprovalRate}%`
                                    : t('notSetUsingTestCards')}
                            </p>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

